import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { findNodeHandle, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Text, useTheme } from '@ui-kitten/components';
import lodashFindIndex from 'lodash/findIndex';

import Hint, { HintProps } from '../../Components/Hint';
import constants from '../../Config/constants';
import { smartJoin } from '../../Helper';
import DateHelper from '../../Helper/Date';
import { setMealPlanHint } from '../../RTK/mealPlan';
import { selectMealPlanHint } from '../../RTK/mealPlan/selectors';
import { shopMenuSelector } from '../../RTK/shop/selectors';
import themeStyle from '../../Theme/styles';

// this is linked to App/RTK/defaultValues.js -> mealPlan.hint
export enum HintKey {
  FEATURED = 'showFeatured',
  EXCLUSIVE = 'showExclusive',
  NEXT = 'showNext',
  PREV = 'showPrev',
  CREATE = 'showCreate',
  REVIEW = 'showReview',
}

type MealPlanHintProps = PropsWithChildren & {
  hintKey: HintKey;
  scrollOffset?: number;
  scrollRef?: HintProps['scrollRef'];
};

type ConditionalMealPlanHintProps = MealPlanHintProps & {
  enabled: boolean;
};

const tipList = {
  [HintKey.FEATURED]:
    'Please select your meal plan items from the menu to get started.', // this will auto scroll and show on the featured item menu
  [HintKey.NEXT]: 'Set meals for the next day or add more to this day.', // this show on next day button on meal plan list
  [HintKey.PREV]: "Go back to yesterday's meals to review or make changes.", // this show on back day button on meal plan list
  [HintKey.EXCLUSIVE]: `Exclusives are available on ${smartJoin(
    constants.MEAL_PLAN_EXCLUSIVE_AVAILABILITY.map(
      DateHelper.convertNumberToDay,
    ),
  )} in the tagged section — other meals can still be added.`, // this will auto scroll and show on meal plan exclusive section on the menu
  [HintKey.CREATE]:
    'All set with your meals? Click to confirm and proceed to checkout.', // this will show on meal plan list when on friday on create meal plan button
  [HintKey.REVIEW]:
    'Want to check your selections? Tap here to review your meal plan.', // only on mobile, show hint on floating button to review meal plan
};

const widthAndHeightList = {
  [HintKey.FEATURED]: { width: 255, height: 89 },
  [HintKey.NEXT]: { width: 250, height: 65 },
  [HintKey.PREV]: { width: 250, height: 65 },
  [HintKey.EXCLUSIVE]: { width: 300, height: null },
  [HintKey.CREATE]: { width: 280, height: 65 },
  [HintKey.REVIEW]: { width: 260, height: 89 },
};

const MealPlanHint = ({
  children,
  hintKey,
  scrollOffset = 0,
  scrollRef,
}: MealPlanHintProps) => {
  const dispatch = useDispatch();
  const mealPlanHintRef = useRef<View>();
  const theme = useTheme();
  const hintKeySelector = useMemo(() => selectMealPlanHint(hintKey), [hintKey]);
  const hintKeyState = useSelector(hintKeySelector);
  const shopMenu = useSelector(shopMenuSelector);
  const [cHeight, setCHeight] = useState<undefined | number>();
  const [cYpos, setCYPos] = useState<undefined | number>();

  const exclusiveSectionIndex = lodashFindIndex(shopMenu, {
    isExclusive: true,
  });

  const scrollProps = useMemo(() => {
    if (constants.isWeb) {
      const totalYPos = cYpos ? cYpos - scrollOffset : 0;
      return totalYPos >= 0 ? { y: totalYPos } : undefined;
    } else if (hintKey === HintKey.FEATURED) {
      return {
        sectionIndex: 0,
        itemIndex: 0,
        viewOffset: scrollOffset + cHeight,
      };
    } else if (hintKey === HintKey.EXCLUSIVE) {
      return {
        sectionIndex: exclusiveSectionIndex,
        itemIndex: 0,
        viewOffset: scrollOffset,
      };
    }
    return undefined;
  }, [scrollOffset, hintKey, cHeight, cYpos, exclusiveSectionIndex]);

  const _dismissHint = useCallback(
    () => dispatch(setMealPlanHint({ [hintKey]: false })),
    [hintKey, dispatch],
  );

  const _onLayoutWeb = useCallback(() => {
    if (mealPlanHintRef.current && scrollRef?.current) {
      mealPlanHintRef.current.measureLayout(
        findNodeHandle(scrollRef.current),
        (_x, y, _width, height) => {
          setTimeout(() => {
            setCYPos(y);
            setCHeight(height);
          }, 0);
        },
        () => console.warn('meal plan hint measurement layout error'),
      );
    }
  }, [scrollRef]);

  return (
    <View ref={mealPlanHintRef} onLayout={_onLayoutWeb}>
      <Hint
        backgroundColor={theme?.button_color}
        show={hintKeyState}
        scrollProps={scrollProps}
        scrollRef={scrollRef}
        tip={
          <Text appearance="alternative" style={themeStyle.flex1}>
            {tipList[hintKey]}
          </Text>
        }
        width={widthAndHeightList[hintKey].width}
        height={widthAndHeightList[hintKey].height}
        onClose={_dismissHint}
      >
        {children}
      </Hint>
    </View>
  );
};

export const ConditionalMealPlanHint = ({
  children,
  enabled,
  ...props
}: ConditionalMealPlanHintProps) => {
  if (!enabled) return children;
  return <MealPlanHint {...props}>{children}</MealPlanHint>;
};

export default MealPlanHint;
